import React, { useEffect, useMemo } from "react";
import { EmailListData, FileUpload } from "../types/emailList.ts";
import { Select, ValueOpt } from "best-common-react";
import { EmailListColumnHeaders } from "../constants/DropdownsConstants.ts";
import styled from "styled-components";

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border: 1px solid black;
  table-layout: fixed;
  border-collapse: collapse;
`;

type TrProps = {
  odd: boolean;
};

const Tr = styled.tr<TrProps>`
  background-color: ${({ odd }) => (odd ? "#f9f9f9" : "white")};
`;

const Th = styled.th`
  width: 200px;
  max-width: 200px;
  padding: 0.5rem;
  border: 1px solid black;
  border-collapse: collapse;
`;

const Td = styled.td`
  width: 200px;
  max-width: 200px;
  padding: 0.5rem;
  border: 1px solid black;
  border-collapse: collapse;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HeaderCell = styled.div``;

const HeaderLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

type TableHeaderProps = {
  id: string;
  label: string;
  value: keyof EmailListData;
  options: ValueOpt<keyof EmailListData>[];
  onChange: (value: keyof EmailListData) => void;
};

const TableHeader: React.FC<TableHeaderProps> = ({ id, label, value, options, onChange }) => {
  return (
    <HeaderCell>
      <HeaderLabel>{label}</HeaderLabel>
      <Select
        id={id}
        value={EmailListColumnHeaders.find(option => option.value === value)}
        options={options}
        onChange={({ value }) => onChange(value)}
      />
    </HeaderCell>
  );
};

type EmailListUploadTableProps = {
  file: FileUpload;
  headers: (keyof EmailListData)[];
  setHeaders: (value: (keyof EmailListData)[]) => void;
  validateEmailAddresses: (colIdx: number) => void;
};

const EmailListUploadTable: React.FC<EmailListUploadTableProps> = ({
  file,
  headers,
  setHeaders,
  validateEmailAddresses
}) => {
  const [displayHeaders, setDisplayHeaders] = React.useState<string[]>([]);
  const numPreviewRows = file.numRows < 5 ? file.numRows : 5;

  const options = useMemo(() => EmailListColumnHeaders.filter(header => !headers.includes(header.value)), [headers])

  useEffect(() => {
    if (!!file.headers) {
      setDisplayHeaders(file.headers);
    } else {
      setDisplayHeaders(Array.from({ length: file.numColumns }, (_, i) => `Column ${i + 1}`));
    }
  }, [file]);

  const handleHeaderChange = (index: number, value: keyof EmailListData) => {
    if (headers.includes(value)) return;

    const temp = [...headers];
    temp[index] = value;
    setHeaders(temp);

    if (value === "email") {
      validateEmailAddresses(index);
    }
  };

  return (
    <TableContainer>
      <Table>
        <Tr odd={false}>
          {[...Array(file.numColumns)].map((_, i) => {
            const id = `th-${i}`;
            return (
              <Th key={id}>
                <TableHeader
                  id={id}
                  label={displayHeaders[i]}
                  value={headers[i]}
                  options={options}
                  onChange={(value: keyof EmailListData) => {
                    handleHeaderChange(i, value);
                  }}
                />
              </Th>
            );
          })}
        </Tr>

        {[...Array(numPreviewRows)].map((_, rowIdx) => (
          <Tr key={`tr-${rowIdx}`} odd={rowIdx % 2 === 0}>
            {file.data[rowIdx].map((cellData, colIdx) => (
              <Td key={`td-col-${colIdx}-row-${rowIdx}`}>{cellData}</Td>
            ))}
          </Tr>
        ))}
      </Table>
    </TableContainer>
  );
};

export default EmailListUploadTable;
